import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { Grid, IconButton, Popover } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { useGetUserMetricsQuery } from '../../../../../services/PaymentToolApi';
import EditTransactionModal from '../components/EditTransactionModal';
import { eventDatesFormatter, parseToEasternTime } from '../../../../../utils/dates';
import { copyToClipBoard, formatMoney } from '../../../../../utils';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { useLazyGetTransactionsQuery } from '../../../../../services/PaymentToolApi';
import { useLazyGetPurchaseQuickViewQuery } from '../../../../../services/purchaseApi';
import { useLazyJobQuery } from '../../../../../services/jobsApi';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { AddErrorTransactionModal } from '../components/AddErrorTransactionModal';
import { EditErrorTransactionModal } from '../components/EditErrorTransactionModal';
import { useGetTransactionDetailsQuery } from "../../../../../services/PaymentToolApi";
import Button from '@mui/material/Button';
import styled from 'styled-components';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import {useAccountsCacheQuery} from "../../../../../services/accountsApi";

const AdditionalInfoStyles = {
  bgcolor: 'background.paper',
  borderRadius: '1em',
  padding: '1em',
}

const TwoColumnGrid = ({ leftChild, rightChild }) => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        {leftChild}
      </Grid>
      <Grid item xs={6} container justifyContent="flex-end">
        {rightChild}
      </Grid>
    </Grid>
  );
};


const AdditionalInfoPopper = ({ open, anchorEl, additionalInfo, onClose }) => {
 
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      PaperProps={{
        onMouseLeave: onClose,
        style: AdditionalInfoStyles
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
        {
            
          (
            <Box>
              <Box>
                <span style={{ color: "var(--orange)", fontWeight: '600' }}>Related Transaction Id: </span><span>{additionalInfo?.isRelated}</span>
              </Box>

              <Box onClick={_ => {copyToClipBoard(additionalInfo.isRelated); onClose()}} className="cursor-pointer hover-orange">
                <b>Click to copy</b>
              </Box>
            </Box>
          )
            
        }
      </Box>
    </Popover>
  )
}


/**
 * Transaction Edit Modals - Documentation
 * ------------------------------
 * This component uses different modals for editing different types of transactions:
 * 
 * 1. EditTransactionModal: Used for manual transactions of types:
 *    - Reward (manual)
 *    - Deduction (manual)
 *    - Bonus (manual)
 *    - Royalty (manual)
 * 
 * 2. EditErrorTransactionModal: Used specifically for:
 *    - Penalty (manual)
 * 
 * 3. AddErrorTransactionModal: Used to create error transactions for:
 *    - Reward transactions (both manual and automatic) without related transactions
 * 
 * Note: EditBonusTransactionModal and EditUserErrorTransactionModal exist in the codebase
 * but are not used in this component. They might be used elsewhere or be deprecated code.
 */
export default function PaymentByUserTable({ selectedYear, selectedPeriod, selectedUser, search }) {

  const [getTransaction] = useLazyGetTransactionsQuery();
  const [getPurchase] = useLazyGetPurchaseQuickViewQuery();
  const [getJob] = useLazyJobQuery();

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [openEditTransactionModal, setOpenEditTransactionModal] = useState(false);
  const [openCreateErrorTransactionModal, setOpenCreateErrorTransactionModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const [isAdditionalInfoOpened, setIsAdditionalInfoOpened] = useState(false);
  const [rowAnchorEl, setRowAnchorEl] = useState(null);
  const [selectedAdditionalInfo, setSelectedAdditionalInfo] = useState(null);

  const { data: accountsApiRes, isLoading: accountsApiIsLoading } = useAccountsCacheQuery();

  const { data: userMetrics, error: userMetricsError, isLoading: userMetricsLoading, isFetching: userMetricsIsFetching } = useGetUserMetricsQuery({
    userId: selectedUser.id,
    year: selectedYear,
    period: selectedPeriod,
    pageNumber: (pageNumber),
    pageSize: pageSize,
    isDeleted: false,
    search: search,
  });

  function onPaginationChangeHandler(model) {
    console.log(model)
    const { page, pageSize: newPageSize } = model;
    if (newPageSize != pageSize) setPageSize(newPageSize);
    let pageGridFixed = page + 1;
    if (pageGridFixed != pageNumber) {
      setPageNumber(pageGridFixed)
    }
  }

  function TransactionIDCopyIcon({ params }) {

    return (
      <Tooltip title="Copy Transaction ID" placement="top-start">
        <IconButton
          sx={{ margin: " 0 0 0 0.25em " }}
          color="primary"
          onClick={() => copyToClipBoard(params.row.transactionID)}
        >
          <ContentCopyOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const history = useHistory();

  function redirectToRequestHistory(transactionId) {
    searchParams.set("search_value", transactionId);
    searchParams.delete("tab_index");
    searchParams.set("is_today_request", false);
  
    const newTabUrl = `/dashboard/adminswarm?tab_index=2&${searchParams.toString()}`;
    window.open(newTabUrl, '_blank'); // Abre en una nueva pestaña
  }
  async function redirectToJobPurchaseForm(transactionId) {
    const transaction = await getTransaction({ TransactionID: transactionId, isDeleted: false }, false).unwrap();
    const job = await getPurchase(`?PurchaseId=${transaction[0].purchaseID}`, false).unwrap()
    const jobId = job[0].requestID;
    const newTabUrl = `/dashboard/admintools/jobs/${jobId}?tab_index=3`;
    window.open(newTabUrl, '_blank');
  }

  function NotesForAdminToolTip({params}){
    let text= "";
    if (params.row.transactionType === "Penalty") {
      //A: if errorReason is "Other" we show only the notesForAdmin, otherwise we show the errorReason and notesForAdmin
      text = params.row.errorReason !== "Other" 
        ? params.row.errorReason + (params.row.notesForAdmin ? ", " + params.row.notesForAdmin : "")
        : params.row.notesForBee + (params.row.notesForAdmin ? ", " + params.row.notesForAdmin : "")
    } else {
      text = params.row.notesForAdmin || "N/A";
    }
    return (
      <Tooltip title={text} placement="top-start">
        <IconButton
          sx={{ margin: "0" }}
          color="primary"
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  const CustomWidthTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      maxWidth: 'none',
    },
  });

  function EventInformationToolTip({params}){
    const [triggerGetTransactionDetails, setTriggerGetTransactionDetails] = useState(false);
    const [text, setText] = useState("Loading..."); 

    const handleMouseEnter = () => {
      setTriggerGetTransactionDetails(true);
    };

    const {
      data: transactionDetails, 
      isLoading: transactionDetailsIsLoading, 
      error: transactionDetailsError} = useGetTransactionDetailsQuery({transactionID: params.row.transactionID, fields: ['orderNumber', 'venueName', 'eventdate']}, {skip: !triggerGetTransactionDetails});

      useEffect(() => {
        if (transactionDetails) {
          let text =
            "Venue Name: " + transactionDetails.venueName + "\n" +
            "Event Date: " + eventDatesFormatter(transactionDetails.eventDate,  { showWeekday: false }) + "\n" +
            "Order Number: " + transactionDetails.orderNumber;
          setText(text);
        }
      }, [transactionDetails]);

    return (
      <CustomWidthTooltip  
        title={
          <span style={{ whiteSpace: 'pre-line' }}>
            {text}
          </span>
        } 
        placement="top-start" 
        onMouseEnter={handleMouseEnter}
      >
          <Button style={{color:'black'}}>{params.row.eventName}</Button>
      </CustomWidthTooltip >
    );
  }

  function getActionButtons(row){

    let CreateErrorTransactionIcon=(
    <Tooltip title="Create Error Transaction" placement="top-start">
      <IconButton
        sx={{ padding: "0" }}
        onClick={() => { setOpenCreateErrorTransactionModal(true); setSelectedTransaction(row) }}
        variant="contained"
        size="small"
        color='error'
      >
        <HighlightOffIcon />
      </IconButton>
    </Tooltip>)

    let EditTransactionIcon=(
      <Tooltip title="Edit Transaction" placement="top-start">
        <IconButton
          sx={{ padding: "0" }}
          onClick={() => { setOpenEditTransactionModal("editTransaction"); setSelectedTransaction(row) }}
          variant="contained"
          size="small"
          color='primary'
        >
          <EditOutlinedIcon />
        </IconButton>
      </Tooltip>)

    let AdditionInfoIcon=(
      <IconButton aria-label="Addition Info"
          sx={{ padding: "0" }}
          color="primary"
          onClick={x => onAdditionalInfoModalClick(x, row)}
          >
          <InfoOutlinedIcon />
        </IconButton>
    )

    // For automatic Rewards without related transactions: Show Create Error Transaction button
    // Uses AddErrorTransactionModal to create error transactions
    if(row.transactionType == "Reward" && !row.manual && !row.isRelated){
      return <TwoColumnGrid leftChild={CreateErrorTransactionIcon} rightChild={<></>} />
    }

    // For automatic Rewards with related transactions: Show Additional Info icon
    if(row.transactionType == "Reward" && !row.manual && row.isRelated){
      return <TwoColumnGrid leftChild={AdditionInfoIcon} rightChild={<></>} />
    }

    // For manual Penalty transactions: Use EditErrorTransactionModal
    // Note: Despite the name suggesting EditTransactionModal, we're actually using EditErrorTransactionModal
    // This is controlled by passing "editPenaltyTransaction" to setOpenEditTransactionModal
    if(row.transactionType == "Penalty" &&row.manual){
      return <TwoColumnGrid rightChild={
          <Tooltip title="Edit Error Transaction" placement="top-start"
          sx={{ padding: "0" }}
          >
            
            <IconButton
              onClick={() => { setOpenEditTransactionModal("editPenaltyTransaction"); setSelectedTransaction(row) }}
              variant="contained"
              size="small"
              color='primary'
            >
              <EditOutlinedIcon />
            </IconButton>
          </Tooltip>
        }
        leftChild={<></>} />
    }

    // For manual Reward transactions without related transactions: 
    // Show both Edit Transaction (EditTransactionModal) and Create Error Transaction buttons
    if(row.transactionType == "Reward" && row.manual && !row.isRelated){
      return <TwoColumnGrid leftChild={CreateErrorTransactionIcon} rightChild={EditTransactionIcon} />
    }
    
    // For manual Reward transactions with related transactions: 
    // Show both Additional Info and Edit Transaction (EditTransactionModal) buttons
    if(row.transactionType == "Reward" && row.manual && row.isRelated){
      return( <TwoColumnGrid leftChild={AdditionInfoIcon} rightChild={EditTransactionIcon} />)
    }

    // For all other manual transactions (Deduction, Bonus, Royalty): 
    // Show Edit Transaction button (EditTransactionModal)
    if(row.manual){
      return <TwoColumnGrid leftChild={<></>} rightChild={EditTransactionIcon} />
    }
  }

  const columns = [
    {
      field: 'transactionID',
      headerName: 'TRANSACTION ID',
      flex: 0.75,
      renderCell: (params) => {
        if(params.row.manual){
          return params.row.transactionID
        }
        else if (params.row.buyType === "HiveBuy") {
          return <p style={{ cursor: "pointer", color: "DodgerBlue", textDecoration: "underline" }} onClick={async () => await redirectToJobPurchaseForm(params.row.transactionID)}>{params.row.transactionID}</p>
        } else if (params.row.buyType === "SwarmBuy") {
          return <p style={{ cursor: "pointer", color: "DodgerBlue", textDecoration: "underline" }} onClick={() => redirectToRequestHistory(params.row.transactionID)}>{params.row.transactionID}</p>
        } else {
          return params.row.transactionID
        }
      }
    },
    {
      headerName: "",
      field: "copyLink",
      width: 45,
      align: "center",
      renderCell: (params) => <TransactionIDCopyIcon params={params} />
    },
    {
      field: 'date',
      headerName: 'DATE',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => parseToEasternTime(params.row.date),
    },
    {
      field: 'eventName',
      headerName: 'EVENT NAME',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        if(!params.row.eventName) return "-";
        
        return (
          
          <EventInformationToolTip params={params} />
            
        );
      }
    },
    {
      field: 'buyType',
      headerName: 'BUY TYPE',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => params.row.buyType || '-',
    },
    {
      field: 'transactionType',
      headerName: 'TRANSACTION TYPE',
      //THIS WIDTH IS IMPORTANT, THIS ALLOR TO SHOW ROWS WITH PERFECT ALIGNMENT BETWEEN THE TEXT AND ICON
      width: 126,
      headerAlign: 'center',
      align: 'left',
      renderCell: (params) => {
        let text= params.row.transactionType == "Penalty" ? "Error" : params.row.transactionType;

        const color = {
          Reward: "green",
          Penalty: "red",
          Deduction: "orange",
          Bonus: "green",
          Royalty: "green",
        }[params.row.transactionType] || "black";

        return (
          <Grid container alignItems="center" justifyContent="space-between" wrap='nowrap'>
            <Grid item style={{ color, fontWeight: "600" }}>
              {text.trim()}
            </Grid>
            <Grid item sx={{ marginLeft: 'auto' }}>
              <NotesForAdminToolTip params={params} />
            </Grid>
          </Grid>
        );
      }
    },
    {
      field: 'relatedTransactionID',
      headerName: 'RELATED TRANSACTION',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => params.row.relatedTransactionID || '-',
    },
    {
      field: 'reward',
      headerName: 'REWARD',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => params.row.reward,
    },
    
    {
      field: 'amount',
      headerName: 'AMOUNT',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const amount = params.row.amount || 0;
        const formattedAmount = formatMoney(amount);
        const isDeductionOrPenalty = params.row.transactionType === "Deduction" || params.row.transactionType === "Penalty";
        return isDeductionOrPenalty ? <p style={{ color: 'red' }}>-{formattedAmount}</p> : formattedAmount;
      }
    },
    {
      field: 'createdByFullName',
      headerName: 'ADDED BY',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => {
        if (accountsApiIsLoading) {
          return 'Loading...';
        }
  
        const row = params.row;
  
        if (!row.manual) {
          return 'Auto';
        } else {
          const creator = accountsApiRes?.res?.accounts.find((account) => account.id === row.createdBy);
          return creator ? `${creator.firstName} ${creator.lastName}` : 'N/A';
        }
      },
    },
    {
      field: 'buttons',
      headerName: '',
      width: 85,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (getActionButtons(params.row))
    },
  ];


  const onAdditionalInfoModalClick = (e, params) => {
    setRowAnchorEl(e.currentTarget);
    setSelectedAdditionalInfo(params);
    setIsAdditionalInfoOpened(true);
  }

  const onAdditionalInfoModalClosed = () => {
    setIsAdditionalInfoOpened(false);
    setRowAnchorEl(null);
    setSelectedAdditionalInfo(null);
  }

  return (
    <Box sx={{
      height: "60vh",
      width: '100%',
      '& .MuiDataGrid-row': {
        borderBottom: '2px solid #f2f2f2',
      },
    }}
    >
      {/* EditTransactionModal: Used for manual Reward, Deduction, Bonus, Royalty transactions */}
      <EditTransactionModal modalIsOpen={openEditTransactionModal == "editTransaction"} setModalIsOpen={setOpenEditTransactionModal} selectedTransaction={selectedTransaction} />

      {/* EditErrorTransactionModal: Used for manual Penalty transactions */}
      <EditErrorTransactionModal modalIsOpen={openEditTransactionModal == "editPenaltyTransaction"} setModalIsOpen={setOpenEditTransactionModal} selectedTransaction={selectedTransaction} />

      {/* AddErrorTransactionModal: Used to create error transactions for Rewards */}
      <AddErrorTransactionModal modalIsOpen={openCreateErrorTransactionModal} setModalIsOpen={setOpenCreateErrorTransactionModal} selectedTransaction={selectedTransaction} />

      <AdditionalInfoPopper
        open={isAdditionalInfoOpened}
        anchorEl={rowAnchorEl}
        additionalInfo={selectedAdditionalInfo}
        onClose={onAdditionalInfoModalClosed}
      />

      <DataGridPremium
        sx={{ borderRadius: "0 0 1em 1em", backgroundColor: 'white', padding: '0 1em', '& .MuiBadge-badge': { display: 'none' }, }}
        headerHeight={40}
        rowHeight={36}
        loading={userMetricsLoading || userMetricsIsFetching}
        rows={
          (userMetrics?.items || []).slice().sort((a, b) => a.transactionID - b.transactionID)
        }
        getRowId={row => row.transactionID}
        columns={columns}
        disableRowSelectionOnClick
        pagination
        paginationMode="server"
        rowCount={userMetrics?.totalCount || 0}
        onPaginationModelChange={onPaginationChangeHandler}
        page={pageNumber - 1} //A: the pagination starts in 0, but the api starts in 1
        pageSize={pageSize}
        initialState={{
          pagination: { paginationModel: { pageSize: pageSize, page: pageNumber - 1 } }, //A: the pagination starts in 0, but the api starts in 1
        }}
      />
    </Box>
  );
}